import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  CreditCard, Check, AlertTriangle, Zap, Crown, Star, Calendar, 
  Receipt, Download, RefreshCw, Building2, BedDouble, Users, Loader2
} from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

import { API } from '@/config/api';

const SubscriptionPage = () => {
  const { getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [upgrading, setUpgrading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [subRes, plansRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/subscription`, { headers: getAuthHeaders() }),
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/invoices`, { headers: getAuthHeaders() })
      ]);
      setSubscription(subRes.data);
      setSubscriptionPlans(plansRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      setCheckingPayment(false);
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/checkout/status/${sessionId}`,
        { headers: getAuthHeaders() }
      );

      if (response.data.payment_status === 'paid') {
        toast.success(response.data.message || 'Payment successful! Your subscription has been upgraded.');
        setCheckingPayment(false);
        // Clear URL params
        setSearchParams({});
        // Refresh subscription data
        fetchData(true);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status. Please refresh the page.');
      setCheckingPayment(false);
    }
  }, [getAuthHeaders, fetchData, setSearchParams]);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');

    if (sessionId) {
      setCheckingPayment(true);
      toast.info('Verifying payment status...');
      pollPaymentStatus(sessionId);
    } else if (canceled) {
      toast.error('Payment was canceled.');
      setSearchParams({});
    }
  }, [searchParams, pollPaymentStatus, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Stripe checkout handler
  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    setUpgrading(true);
    try {
      console.log('Starting checkout for plan:', selectedPlan, 'billing:', selectedBillingCycle);
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan: selectedPlan,
          billing_cycle: selectedBillingCycle,
          success_url: `${window.location.origin}/subscription?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/subscription?canceled=true`
        },
        { headers: getAuthHeaders() }
      );
      
      console.log('Checkout response:', response.data);
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        console.log('Redirecting to:', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
      } else {
        console.error('Response data:', response.data);
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(getErrorMessage(error, 'Failed to initiate checkout'));
      setUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) return;
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { headers: getAuthHeaders() });
      toast.success('Subscription canceled');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel subscription'));
    }
  };

  const getPlanIcon = (plan) => {
    switch (plan) {
      case 'starter': return <Star className="h-6 w-6 text-amber-500" />;
      case 'professional': return <Zap className="h-6 w-6 text-purple-600" />;
      case 'premium': return <Crown className="h-6 w-6 text-amber-500" />;
      default: return <Calendar className="h-6 w-6 text-blue-600" />;
    }
  };

  if (loading || checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="subscription-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">
          {checkingPayment ? 'Verifying payment status...' : 'Loading subscription...'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit']">Subscription & Billing</h1>
          <p className="text-muted-foreground">Manage your plan, usage, and invoices</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Current Plan Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plan Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subscription && (
              <div className="space-y-6">
                {/* Plan Banner */}
                <div className={`p-6 rounded-xl ${
                  subscription.status === 'trial' ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                  subscription.status === 'active' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' :
                  'bg-gradient-to-r from-amber-500 to-amber-600'
                } text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white/20 rounded-lg">
                        {getPlanIcon(subscription.plan)}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold capitalize">
                          {subscription.plan_config?.name || subscription.plan}
                        </h2>
                        <p className="text-white/80">
                          {subscription.status === 'trial' ? (
                            subscription.is_trial_expired ? 'Trial expired' : `${subscription.days_remaining} days remaining`
                          ) : subscription.status === 'active' ? (
                            `Renews in ${subscription.days_remaining} days`
                          ) : (
                            <span className="capitalize">{subscription.status}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    {(subscription.status === 'trial' || subscription.status === 'expired') && (
                      <Button 
                        variant="secondary" 
                        onClick={() => { setSelectedPlan('professional'); setUpgradeDialogOpen(true); }}
                        data-testid="upgrade-btn"
                      >
                        Upgrade Now
                      </Button>
                    )}
                  </div>
                </div>

                {/* Trial Warning */}
                {subscription.status === 'trial' && subscription.is_trial_expired && (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-200">Your trial has expired</p>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        You can still view your data, but cannot create new reservations, guests, or rooms. Upgrade to continue.
                      </p>
                    </div>
                  </div>
                )}

                {/* Features */}
                <div>
                  <h4 className="font-medium mb-3">Plan Features</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {subscription.plan_config?.features?.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-muted/50">
                        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                        <span className="capitalize">{feature.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cancel */}
                {subscription.status === 'active' && (
                  <div className="pt-4 border-t">
                    <Button variant="ghost" className="text-destructive" onClick={handleCancelSubscription}>
                      Cancel Subscription
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Usage</CardTitle>
            <CardDescription>Current resource usage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span>Hotels</span>
                </div>
                <span className="font-medium">
                  {subscription?.hotels_count} / {subscription?.plan_config?.max_hotels === -1 ? '∞' : subscription?.plan_config?.max_hotels}
                </span>
              </div>
              <Progress 
                value={subscription?.plan_config?.max_hotels === -1 ? 10 : (subscription?.hotels_count / subscription?.plan_config?.max_hotels) * 100} 
                className="h-2"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <BedDouble className="h-4 w-4 text-muted-foreground" />
                  <span>Rooms</span>
                </div>
                <span className="font-medium">
                  {subscription?.rooms_count} / {subscription?.plan_config?.max_rooms_per_hotel === -1 ? '∞' : subscription?.plan_config?.max_rooms_per_hotel * Math.max(1, subscription?.hotels_count)}
                </span>
              </div>
              <Progress 
                value={subscription?.plan_config?.max_rooms_per_hotel === -1 ? 10 : (subscription?.rooms_count / (subscription?.plan_config?.max_rooms_per_hotel * Math.max(1, subscription?.hotels_count))) * 100} 
                className="h-2"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>Staff</span>
                </div>
                <span className="font-medium">
                  {subscription?.staff_count} / {subscription?.plan_config?.max_staff === -1 ? '∞' : subscription?.plan_config?.max_staff}
                </span>
              </div>
              <Progress 
                value={subscription?.plan_config?.max_staff === -1 ? 10 : (subscription?.staff_count / subscription?.plan_config?.max_staff) * 100} 
                className="h-2"
              />
            </div>

            {subscription?.plan !== 'premium' && (
              <div className="pt-4">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => { setSelectedPlan('premium'); setUpgradeDialogOpen(true); }}
                >
                  Need more? Upgrade
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Available Plans</CardTitle>
          <CardDescription>Compare and choose the right plan for your business</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Starter */}
            <div className={`border rounded-xl p-6 transition-all hover:shadow-lg ${subscription?.plan === 'starter' ? 'border-primary ring-2 ring-primary/20' : ''}`}>
              <div className="flex items-center gap-2 mb-4">
                <Star className="h-6 w-6 text-amber-500" />
                <h3 className="font-bold text-xl">Starter</h3>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold">€29</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">Or €290/year (save €58)</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> 1 hotel</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Up to 20 rooms</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Up to 5 staff</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Core ERP features</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Manual transfers</li>
              </ul>
              {subscription?.plan === 'starter' ? (
                <Badge className="w-full justify-center py-2">Current Plan</Badge>
              ) : (
                <Button variant="outline" className="w-full" onClick={() => { setSelectedPlan('starter'); setUpgradeDialogOpen(true); }}>
                  {subscription?.plan === 'trial' ? 'Select' : 'Downgrade'}
                </Button>
              )}
            </div>

            {/* Professional */}
            <div className={`border-2 rounded-xl p-6 relative transition-all hover:shadow-lg ${subscription?.plan === 'professional' ? 'border-primary ring-2 ring-primary/20' : 'border-purple-300 dark:border-purple-700'}`}>
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600">Most Popular</Badge>
              <div className="flex items-center gap-2 mb-4">
                <Zap className="h-6 w-6 text-purple-600" />
                <h3 className="font-bold text-xl">Professional</h3>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold">€69</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">Or €690/year (save €138)</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Up to 3 hotels</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Up to 80 rooms/hotel</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Up to 15 staff</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Advanced reports</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Drivers & vehicles</li>
              </ul>
              {subscription?.plan === 'professional' ? (
                <Badge className="w-full justify-center py-2">Current Plan</Badge>
              ) : (
                <Button className="w-full bg-purple-600 hover:bg-purple-700" onClick={() => { setSelectedPlan('professional'); setUpgradeDialogOpen(true); }}>
                  {subscription?.plan === 'premium' ? 'Downgrade' : 'Upgrade'}
                </Button>
              )}
            </div>

            {/* Premium */}
            <div className={`border rounded-xl p-6 transition-all hover:shadow-lg ${subscription?.plan === 'premium' ? 'border-primary ring-2 ring-primary/20' : 'border-amber-200 dark:border-amber-800'}`}>
              <div className="flex items-center gap-2 mb-4">
                <Crown className="h-6 w-6 text-amber-500" />
                <h3 className="font-bold text-xl">Premium</h3>
              </div>
              <div className="mb-2">
                <span className="text-4xl font-bold">€129</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">Or €1,290/year (save €258)</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Up to 10 hotels</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Unlimited rooms</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Unlimited staff</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> Accounting exports</li>
                <li className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /> API access</li>
              </ul>
              {subscription?.plan === 'premium' ? (
                <Badge className="w-full justify-center py-2">Current Plan</Badge>
              ) : (
                <Button variant="outline" className="w-full border-amber-500 text-amber-600 hover:bg-amber-50" onClick={() => { setSelectedPlan('premium'); setUpgradeDialogOpen(true); }}>
                  Upgrade
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Billing History
          </CardTitle>
          <CardDescription>Your subscription invoices and payments</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{format(new Date(invoice.invoice_date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>Subscription - {invoice.subscription_id?.slice(0, 8)}</TableCell>
                    <TableCell className="font-medium">€{invoice.amount?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={invoice.status === 'paid' ? 'default' : invoice.status === 'pending' ? 'secondary' : 'destructive'}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No invoices yet</p>
              <p className="text-sm">Your billing history will appear here after your first payment</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
            <DialogDescription>Select your billing cycle and complete the upgrade</DialogDescription>
          </DialogHeader>
          
          {selectedPlan && subscriptionPlans[selectedPlan] && (
            <div className="space-y-6">
              <div className="p-4 rounded-lg bg-muted/50 flex items-center gap-3">
                {getPlanIcon(selectedPlan)}
                <div>
                  <h3 className="font-semibold text-lg">{subscriptionPlans[selectedPlan].name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {subscriptionPlans[selectedPlan].max_hotels === -1 ? 'Unlimited' : subscriptionPlans[selectedPlan].max_hotels} hotels • 
                    {subscriptionPlans[selectedPlan].max_rooms_per_hotel === -1 ? ' Unlimited' : ` ${subscriptionPlans[selectedPlan].max_rooms_per_hotel}`} rooms • 
                    {subscriptionPlans[selectedPlan].max_staff === -1 ? ' Unlimited' : ` ${subscriptionPlans[selectedPlan].max_staff}`} staff
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Billing Cycle</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedBillingCycle === 'monthly' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedBillingCycle('monthly')}
                  >
                    <div className="font-medium">Monthly</div>
                    <div className="text-2xl font-bold">€{subscriptionPlans[selectedPlan].price_monthly}</div>
                    <div className="text-sm text-muted-foreground">per month</div>
                  </div>
                  <div 
                    className={`p-4 border rounded-lg cursor-pointer transition-all relative ${
                      selectedBillingCycle === 'yearly' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedBillingCycle('yearly')}
                  >
                    <Badge className="absolute -top-2 -right-2 bg-emerald-600">
                      Save €{subscriptionPlans[selectedPlan].price_monthly * 12 - subscriptionPlans[selectedPlan].price_yearly}
                    </Badge>
                    <div className="font-medium">Yearly</div>
                    <div className="text-2xl font-bold">€{subscriptionPlans[selectedPlan].price_yearly}</div>
                    <div className="text-sm text-muted-foreground">per year</div>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                  <CreditCard className="h-4 w-4" />
                  <p className="text-sm">
                    <strong>Secure Payment:</strong> You'll be redirected to Stripe for secure payment processing.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialogOpen(false)} disabled={upgrading}>Cancel</Button>
            <Button onClick={handleUpgrade} disabled={upgrading} data-testid="confirm-upgrade-btn">
              {upgrading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting to payment...
                </>
              ) : (
                `Pay €${selectedPlan && subscriptionPlans[selectedPlan] ? 
                  (selectedBillingCycle === 'yearly' ? subscriptionPlans[selectedPlan].price_yearly : subscriptionPlans[selectedPlan].price_monthly) 
                  : ''} - ${selectedPlan ? subscriptionPlans[selectedPlan]?.name : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
